import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import '../index.css';
import Header from "./nav_panel/Header";
import LeftSidebar from "./nav_panel/LeftSidebar";
import AssemblyTopBar from "./nav_panel/AssemblyTopBar";
import HomePage from "./home_page/HomePage";
import Login from "./auth_page/Login";
import ForgotPassword from "./auth_page/ForgotPassword";
import ResetPassword from "./auth_page/ResetPassword";
import Signup from "./auth_page/Signup";
import FeedPage from "./feed_page/FeedPage";
import ContentDetailPage from "./feed_page/ContentDetailPage";
import ExplorePage from "./explore_page/ExplorePage";
import SharedMapLayer from "./explore_page/SharedMapLayer";
import CreatePost from "./create_page/CreatePost";
import CreateEvent from "./create_page/CreateEvent";
import MessagingPage from "./messaging_page/MessagingPage";
import MessageDock from "./messaging_page/message_modal/MessageDock";
import ProfilePage from "./profile_page/ProfilePage";
import OrganizationProfilePage from "./profile_page/OrganizationProfilePage";
import OrganizationPage from "./organization_page/OrganizationPage";
import OrganizationChannelsPage from "./organization_channels_page/OrganizationChannelsPage";
import SettingsPage from "./settings_page/SettingsPage";
import NotificationsPage from "./notifications_page/NotificationsPage";
import NetworkPage from "./network_page/NetworkPage";
import ManageConnectionsPage from "./network_page/ManageConnectionsPage";
import ManageAffiliationsPage from "./network_page/ManageAffiliationsPage";
import BetaHubPage from "./beta_page/BetaHubPage";
import SurveyStartPage from "./beta_page/SurveyStartPage";
import SurveyTakePage from "./beta_page/SurveyTakePage";
import SurveyThanksPage from "./beta_page/SurveyThanksPage";
import FeedbackPage from "./feedback_page/FeedbackPage";
import PublicFeedbackThanksPage from "./feedback_page/PublicFeedbackThanksPage";
import { UserRealtimeProvider } from "../context/UserRealtimeContext";
import { MessagingSearchProvider } from "../context/MessagingSearchContext";
import NewOrgPage from "./new_org_page/NewOrgPage";
import NewUserPage from "./new_user_page/NewUserPage";
import JoinPage from "./join_page/JoinPage";
import ProfileSetupPage from "./join_page/ProfileSetupPage";
import CreateAccountPage from "./signup/CreateAccountPage";
import NotFoundPage from "./NotFoundPage";
import AdminApp from "./admin_page/AdminApp";
import { availableThemeIds, THEME_SYSTEM } from "./utils";
import { getUser, getOrganization } from "../api/api";
import {
    dropActiveAccount,
    firstWorkingAccount,
    rehydrateSavedAccounts,
    signOutAccount,
    signOutEveryAccount,
    switchToSavedAccount,
} from "../lib/sessionActions";
import {
    accountHomePath,
    clearRoster,
    clearStoredSession,
    getRoster,
    getSessionToken,
    restoreActiveSessionToken,
    setSessionToken,
    updateActiveProfile,
} from "../lib/sessionToken";
import { useEffectiveTheme } from "../hooks/useEffectiveTheme";
import { cn } from "../lib/utils";
import { isAuthHeroPath } from "../lib/authHero";
import { settingsFromResponse, themeFromAccount } from "../lib/accountSettings";

const SHELL_NAV_RAIL = 56;

/**
 * App routes shared by logged-in shell and logged-out header layout.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function AppRoutes({ userId, user, refreshUser, theme, setTheme, onSetUser, onSettingsSaved, stageSession, onAccountRemoved }) {
    return (
        <Routes>
            <Route path="/" element={<HomePage userId={userId} isLive />} />
            <Route path="/login" element={<Login stageSession={stageSession} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<Signup onSetUser={onSetUser} />} />
            <Route path="/invitations/accept" element={<Signup onSetUser={onSetUser} inviteMode />} />
            <Route path="/feed" element={<FeedPage userId={userId} user={user} />} />
            <Route
                path="/post/:postId"
                element={<ContentDetailPage userId={userId} user={user} contentType="post" />}
            />
            <Route
                path="/event/:eventId"
                element={<ContentDetailPage userId={userId} user={user} contentType="event" />}
            />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/network" element={<NetworkPage userId={userId} user={user} />} />
            <Route
              path="/network/connections"
              element={<ManageConnectionsPage userId={userId} user={user} />}
            />
            <Route
              path="/network/affiliations"
              element={
                <ManageAffiliationsPage userId={userId} user={user} refreshUser={refreshUser} />
              }
            />
            <Route path="/create-post" element={<CreatePost userId={userId} user={user} />} />
            <Route path="/create-event" element={<CreateEvent userId={userId} user={user} />} />
            <Route path="/messaging/:chatId?" element={<MessagingPage userId={userId} user={user} />} />
            <Route path="/profile/:profileUserId" element={<ProfilePage userId={userId} selfUser={user} refreshUser={refreshUser} />} />
            <Route path="/organization-admin/*" element={<OrganizationPage userId={userId} user={user} refreshUser={refreshUser} />} />
            <Route
              path="/organization/:orgId/channels/:chatId?"
              element={<OrganizationChannelsPage userId={userId} user={user} />}
            />
            <Route path="/organization/:orgId" element={<OrganizationProfilePage userId={userId} user={user} refreshUser={refreshUser} />} />
            <Route path="/settings/*" element={<SettingsPage userId={userId} user={user} theme={theme} setTheme={setTheme} onSettingsSaved={onSettingsSaved} onSetUser={onSetUser} onAccountRemoved={onAccountRemoved} />} />
            <Route path="/notifications/*" element={<NotificationsPage userId={userId} user={user} refreshUser={refreshUser} />} />
            <Route path="/beta" element={userId ? <BetaHubPage userId={userId} user={user} /> : <Navigate to="/feedback" replace />} />
            <Route path="/beta/:surveyId/take" element={userId ? <SurveyTakePage userId={userId} user={user} /> : <Navigate to="/feedback" replace />} />
            <Route path="/beta/:surveyId/thanks" element={userId ? <SurveyThanksPage userId={userId} user={user} /> : <Navigate to="/feedback" replace />} />
            <Route path="/beta/:surveyId" element={userId ? <SurveyStartPage userId={userId} user={user} /> : <Navigate to="/feedback" replace />} />
            <Route path="/feedback/thanks" element={<PublicFeedbackThanksPage userId={userId} user={user} />} />
            <Route path="/feedback" element={<FeedbackPage userId={userId} user={user} />} />
            <Route path="/create-account" element={<CreateAccountPage />} />
            <Route path="/new-org" element={<NewOrgPage onSetUser={onSetUser} />} />
            <Route path="/new-user" element={<NewUserPage onSetUser={onSetUser} />} />
            <Route path="/join" element={<JoinPage stageSession={stageSession} />} />
            <Route path="/profile-setup" element={<ProfileSetupPage stageSession={stageSession} />} />
            <Route path="*" element={<NotFoundPage userId={userId} />} />
        </Routes>
    );
}

/**
 * Logged-in shell: header + rail. Explore is full-bleed under the rail
 * so the map shows through when collapsed; other routes keep the rail spacer.
 * Map lives in MainAppShell so logout does not remount Mapbox.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function LoggedInShell({
    userId,
    user,
    onSetUser,
    mobileNavOpen,
    setMobileNavOpen,
    routeProps,
    savedAccounts,
    activeAccountKey,
    onSwitchAccount,
    onSignOut,
    onSignOutAll,
}) {
    const location = useLocation();
    const isExplore = location.pathname === "/explore";
    const isHome = location.pathname === "/";
    const isLogin = isAuthHeroPath(location.pathname);
    const isMapRoute = isHome || isLogin || isExplore;

    return (
        <UserRealtimeProvider userId={userId} user={user}>
        <MessagingSearchProvider>
        <div
            className={cn(
                "relative h-dvh max-w-full overflow-hidden overscroll-none font-ui text-text-primary",
                // Transparent on home/explore so the sibling SharedMapLayer shows through.
                !isMapRoute && "bg-ui-background"
            )}
        >
            <header className="fixed inset-x-0 top-0 z-40 max-w-full overflow-x-clip border-b border-ui-border bg-ui-card text-text-primary">
                <AssemblyTopBar
                    userId={userId}
                    user={user}
                    onMobileNavOpen={() => setMobileNavOpen(true)}
                    savedAccounts={savedAccounts}
                    activeAccountKey={activeAccountKey}
                    onSwitchAccount={onSwitchAccount}
                    onSignOut={onSignOut}
                    onSignOutAll={onSignOutAll}
                />
            </header>

            {/* Sidebars are shell siblings so their z-30 sits above the explore map (z-20). */}
            <LeftSidebar user={user} variant="desktop" />
            <LeftSidebar
                user={user}
                variant="mobile"
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
            />

            <div
                className={cn(
                    // Sit below the fixed header so the page scrollbar is not covered.
                    "relative z-10 mt-14 flex h-[calc(100dvh-3.5rem)] overflow-hidden",
                    // Click-through to the map on explore; map layer is z-20 above this.
                    isExplore && "pointer-events-none"
                )}
            >
                {/* Reserve rail width except on Explore (map goes under glass sidebar). */}
                <div
                    className={cn("hidden shrink-0 md:block", isExplore && "md:hidden")}
                    style={{ width: SHELL_NAV_RAIL }}
                    aria-hidden
                />

                <main
                    className={cn(
                        "h-full min-h-0 min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto overscroll-x-none touch-pan-y list-scrollbar",
                        // Solid page chrome covers the map on non-map routes.
                        !isMapRoute && "bg-ui-background"
                    )}
                >
                    <AppRoutes {...routeProps} />
                </main>
            </div>

            <MessageDock userId={userId} user={user} userType={user?.type} userPfpUrl={user?.pfp_url} />
        </div>
        </MessagingSearchProvider>
        </UserRealtimeProvider>
    );
}

/**
 * Logged-out layout: top Header + scrollable routes.
 * Map lives in MainAppShell so login does not remount Mapbox.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function LoggedOutShell({
    userId,
    user,
    onSetUser,
    theme,
    setTheme,
    effectiveTheme,
    navHeight,
    headerRef,
    setUserId,
    routeProps,
}) {
    const location = useLocation();
    const isExplore = location.pathname === "/explore";
    const isHome = location.pathname === "/";
    const isLogin = isAuthHeroPath(location.pathname);
    const isJoin = location.pathname === "/join" || location.pathname === "/profile-setup";
    const isMapRoute = isHome || isLogin || isExplore;

    return (
        <div
            className={cn(
                "relative flex h-dvh max-w-full flex-col overflow-hidden overscroll-none",
                // Transparent on home/explore so the sibling SharedMapLayer shows through.
                !isMapRoute && !isJoin && "bg-ui-background",
                isJoin && "bg-primary"
            )}
        >
            {isJoin ? null : (
            <div className="relative z-40">
                <Header
                    ref={headerRef}
                    userId={userId}
                    user={user}
                    setUserId={(id) => {
                        if (!id) onSetUser(null);
                        else setUserId(id);
                    }}
                    theme={theme}
                    setTheme={setTheme}
                    effectiveTheme={effectiveTheme}
                />
            </div>
            )}
            {/* Spacer matches the fixed header so the page scrollbar starts below it. */}
            {isJoin ? null : <div className="shrink-0" style={{ height: navHeight }} aria-hidden />}

            <div
                className={cn(
                    "relative z-10 min-h-0 min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto overscroll-x-none touch-pan-y list-scrollbar",
                    isExplore && "pointer-events-none",
                    !isMapRoute && !isJoin && "bg-ui-background",
                    isJoin && "bg-primary"
                )}
            >
                <AppRoutes {...routeProps} />
            </div>

            <MessageDock userId={userId} user={user} userType={user?.type} userPfpUrl={user?.pfp_url} />
        </div>
    );
}

const LOGGED_IN_HEADER_OFFSET_PX = 56;

/**
 * Single map + auth chrome. SharedMapLayer stays mounted across login/logout
 * so Mapbox is not rebuilt when the shell swaps.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function MainAppShell({
    userId,
    user,
    onSetUser,
    theme,
    setTheme,
    effectiveTheme,
    mobileNavOpen,
    setMobileNavOpen,
    navHeight,
    headerRef,
    setUserId,
    routeProps,
    pendingSessionRef,
    savedAccounts,
    activeAccountKey,
    refreshRoster,
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const loggedIn = Boolean(userId);

    // Admin login borrows the bearer-token slot. Put the saved account back
    // when the main site mounts, and drop a leftover admin token when no
    // person or organization session is saved.
    useEffect(() => {
        if (restoreActiveSessionToken()) return;
        if (!getSessionToken() || getRoster().accounts.length) return;
        setSessionToken("");
        // Once, when the main site mounts — including after leaving the admin portal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Login stages the account, then navigates. Router updates are transitions,
    // so applying the user immediately remounts this shell on /login before the
    // feed paints. Commit the session here, after the pathname has left the
    // auth screen and before the browser paints.
    useLayoutEffect(() => {
        if (isAuthHeroPath(location.pathname)) return;
        const pending = pendingSessionRef.current;
        if (!pending?.id) return;
        pendingSessionRef.current = null;
        onSetUser(pending);
    }, [location.pathname, onSetUser, pendingSessionRef]);

    /**
     * @param {import("../lib/sessionToken").SavedAccount} row
     * @returns {Promise<void>}
     */
    async function handleSwitchAccount(row) {
        if (row.key === activeAccountKey) {
            navigate(accountHomePath(row.type));
            return;
        }
        const outcome = await switchToSavedAccount(row);
        if (outcome.rejected) {
            refreshRoster();
            return;
        }
        if (!outcome.data) return;
        onSetUser(outcome.data);
        navigate(outcome.path);
    }

    /**
     * Revoke the active account and continue as the next saved one.
     * @returns {Promise<void>}
     */
    async function handleSignOut() {
        await signOutAccount();
        const outcome = await firstWorkingAccount();
        if (!outcome.data) {
            onSetUser(null);
            navigate("/");
            return;
        }
        onSetUser(outcome.data);
        navigate(outcome.path || accountHomePath(outcome.data.type));
    }

    /**
     * Revoke every saved session.
     * @returns {Promise<void>}
     */
    async function handleSignOutAll() {
        await signOutEveryAccount();
        onSetUser(null);
        navigate("/");
    }

    /**
     * Drop the active account after it was deleted. Its tokens are already revoked.
     * @returns {Promise<string>}
     */
    async function handleAccountRemoved() {
        dropActiveAccount();
        const outcome = await firstWorkingAccount();
        if (!outcome.data) {
            onSetUser(null);
            return "/";
        }
        onSetUser(outcome.data);
        return outcome.path || accountHomePath(outcome.data.type);
    }

    const shellRouteProps = {
        ...routeProps,
        onAccountRemoved: handleAccountRemoved,
    };

    return (
        <>
            <SharedMapLayer
                userId={userId}
                user={user}
                topOffsetPx={loggedIn ? LOGGED_IN_HEADER_OFFSET_PX : navHeight}
            />
            {loggedIn ? (
                <LoggedInShell
                    key={activeAccountKey || "account"}
                    userId={userId}
                    user={user}
                    onSetUser={onSetUser}
                    mobileNavOpen={mobileNavOpen}
                    setMobileNavOpen={setMobileNavOpen}
                    routeProps={shellRouteProps}
                    savedAccounts={savedAccounts}
                    activeAccountKey={activeAccountKey}
                    onSwitchAccount={handleSwitchAccount}
                    onSignOut={handleSignOut}
                    onSignOutAll={handleSignOutAll}
                />
            ) : (
                <LoggedOutShell
                    userId={userId}
                    user={user}
                    onSetUser={onSetUser}
                    theme={theme}
                    setTheme={setTheme}
                    effectiveTheme={effectiveTheme}
                    navHeight={navHeight}
                    headerRef={headerRef}
                    setUserId={setUserId}
                    routeProps={shellRouteProps}
                />
            )}
        </>
    );
}

function App() {
    const [userId, setUserId] = useState(0);
    const [user, setUser] = useState(null);
    const [roster, setRoster] = useState(() => getRoster());
    const [theme, setTheme] = useState(THEME_SYSTEM);
    const effectiveTheme = useEffectiveTheme(theme);
    const [navHeight, setNavHeight] = useState(LOGGED_IN_HEADER_OFFSET_PX);
    const [sessionReady, setSessionReady] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const headerRef = useRef(null);
    const pendingSessionRef = useRef(null);

    /**
     * Hold a successful login until navigation leaves the auth screen.
     * @param {Object} nextUser Authenticated account from the login response
     */
    function stageSession(nextUser) {
        pendingSessionRef.current = nextUser;
    }

    useEffect(() => {
        availableThemeIds.forEach((t) => {
            document.documentElement.classList.remove(t);
        });
        if (effectiveTheme && availableThemeIds.includes(effectiveTheme)) {
            document.documentElement.classList.add(effectiveTheme);
        }
    }, [effectiveTheme]);

    useEffect(() => {
        let cancelled = false;

        async function rehydrateSession() {
            const onAdmin = window.location.pathname.startsWith("/platform-admin");
            const outcome = await rehydrateSavedAccounts({ commitToken: !onAdmin });
            if (cancelled) return;
            if (outcome.data) {
                onSetUser(outcome.data);
            } else if (outcome.signedOut && !onAdmin) {
                onSetUser(null);
            } else if (!onAdmin && !getSessionToken()) {
                clearStoredSession();
            }
            setSessionReady(true);
        }

        rehydrateSession();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, []);

    useEffect(() => {
        if (userId) return undefined;

        const updateNavHeight = () => {
            if (headerRef.current) {
                setNavHeight(headerRef.current.offsetHeight);
            }
        };

        updateNavHeight();
        window.addEventListener("resize", updateNavHeight);
        return () => window.removeEventListener("resize", updateNavHeight);
    }, [userId, user]);

    async function refreshUser() {
        let result;
        if (user?.type === "user") {
            result = await getUser(userId, userId);
        } else if (user?.type === "organization") {
            result = await getOrganization(userId, userId);
        } else {
            console.error("Invalid user type: ", user?.type);
            return;
        }
        if (result.error) {
            console.error(result.error);
        } else {
            onSetUser(result.data);
        }
    }

    /**
     * @returns {void}
     */
    function refreshRoster() {
        setRoster(getRoster());
    }

    /**
     * Apply the signed-in account. Passing null clears every saved session.
     * @param {Object|null} nextUser
     * @returns {void}
     */
    function onSetUser(nextUser) {
        if (!nextUser?.id) {
            clearRoster();
            setUser(null);
            setUserId(0);
            setRoster(getRoster());
            setTheme(THEME_SYSTEM);
            return;
        }
        const type = nextUser.type === "organization" ? "organization" : "user";
        const stored = { ...nextUser, type };
        updateActiveProfile(stored);
        setUser(stored);
        setUserId(stored.id);
        setTheme(themeFromAccount(stored));
        setRoster(getRoster());
    }

    /**
     * Apply a settings document returned from the API onto the signed-in account.
     * @param {Object} saved
     */
    function applySavedSettings(saved) {
        const nextSettings = settingsFromResponse(saved);
        setUser((prev) =>
            prev ? { ...prev, settings: { ...(prev.settings || {}), ...nextSettings } } : prev
        );
    }

    const routeProps = {
        userId,
        user,
        refreshUser,
        theme,
        setTheme,
        onSetUser,
        onSettingsSaved: applySavedSettings,
        stageSession,
    };

    if (!sessionReady) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-paper text-text-muted">
                Loading…
            </div>
        );
    }

    return (
        <Router>
            <Routes>
                <Route path="/platform-admin/*" element={<AdminApp />} />
                <Route
                    path="*"
                    element={
                        <MainAppShell
                            userId={userId}
                            user={user}
                            onSetUser={onSetUser}
                            theme={theme}
                            setTheme={setTheme}
                            effectiveTheme={effectiveTheme}
                            mobileNavOpen={mobileNavOpen}
                            setMobileNavOpen={setMobileNavOpen}
                            navHeight={navHeight}
                            headerRef={headerRef}
                            setUserId={setUserId}
                            routeProps={routeProps}
                            pendingSessionRef={pendingSessionRef}
                            savedAccounts={[...roster.accounts].sort((a, b) => {
                                if (a.key === roster.activeKey) return -1;
                                if (b.key === roster.activeKey) return 1;
                                return (b.lastUsed || 0) - (a.lastUsed || 0);
                            })}
                            activeAccountKey={roster.activeKey}
                            refreshRoster={refreshRoster}
                        />
                    }
                />
            </Routes>
        </Router>
    );
}

export default App;
